import { isPositionInFence } from '@/lib/wikiLinkSyntax';

export interface WikilinkTrigger {
  /** Character offset where the triggering `[[` begins. */
  start: number;
  /** Character offset of the caret (end of the query typed so far). */
  end: number;
  /** Text typed after `[[` (before any `|` or closing `]]`). */
  query: string;
}

/**
 * Detects whether the caret sits inside an in-progress `[[...` wikilink
 * (i.e. the user has typed `[[` and is now typing the article name, with no
 * closing `]]` yet before the caret) and, if so, returns the query text
 * typed so far.
 *
 * Returns `undefined` when the caret isn't in such a context — including
 * inside a fenced code block, inside an already-closed `[[...]]`, after a
 * `|` (display-text portion, which shouldn't trigger article suggestions),
 * or when a newline / another `[[` interrupts the run.
 */
export function detectWikilinkTrigger(value: string, caret: number): WikilinkTrigger | undefined {
  if (isPositionInFence(value, caret)) return undefined;

  const upToCaret = value.slice(0, caret);
  const lastOpen = upToCaret.lastIndexOf('[[');
  if (lastOpen === -1) return undefined;

  const between = upToCaret.slice(lastOpen + 2, caret);

  // A closing `]]` already exists between the opener and the caret — not
  // an active trigger (the caret has moved past the link).
  if (between.includes(']]')) return undefined;
  // Newlines end the run — wikilinks don't span lines.
  if (between.includes('\n')) return undefined;
  // A `[` inside the query means this isn't a clean `[[` opener (e.g.
  // `[[[` or a stray bracket) — bail rather than guess.
  if (between.includes('[') || between.includes(']')) return undefined;
  // Once the user types `|` they're writing display text, not an article
  // name — stop suggesting articles.
  if (between.includes('|')) return undefined;

  return { start: lastOpen, end: caret, query: between };
}

export interface WikilinkSuggestion {
  /** Normalized `d`-tag slug this suggestion resolves to. */
  slug: string;
  /** Display title shown in the dropdown. */
  title: string;
  /** Number of distinct authors who have published a version of this article. */
  versionCount: number;
}

/** Ranks suggestions: prefix matches first, then substring matches, both alphabetically; caps the result count. */
export function rankWikilinkSuggestions(
  suggestions: WikilinkSuggestion[],
  query: string,
  limit = 8,
): WikilinkSuggestion[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return suggestions.slice(0, limit);

  const scored = suggestions
    .filter((s) => s.title.toLowerCase().includes(needle) || s.slug.includes(needle))
    .map((s) => {
      const title = s.title.toLowerCase();
      const rank = title === needle ? 0 : title.startsWith(needle) ? 1 : 2;
      return { s, rank };
    })
    .sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.s.title.localeCompare(b.s.title)));

  return scored.slice(0, limit).map((x) => x.s);
}

/** Applies a chosen suggestion to the editor text, replacing the in-progress `[[query` with a completed `[[Title]]`. */
export function applyWikilinkSuggestion(
  value: string,
  trigger: WikilinkTrigger,
  suggestion: WikilinkSuggestion,
): { value: string; caret: number } {
  const insertion = `[[${suggestion.title}]]`;
  const newValue = value.slice(0, trigger.start) + insertion + value.slice(trigger.end);
  return { value: newValue, caret: trigger.start + insertion.length };
}
